import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatTRY } from "@/lib/api";
import { Plus, Play, Eye, Trash2, ToggleLeft, ToggleRight, Undo2, History, Sparkles, X, AlertTriangle, CheckCircle2, TrendingUp, ArrowLeft } from "lucide-react";

const TRIGGER_LABELS = {
    rooms_sold: "Satılan Oda Sayısı",
    occupancy_percent: "Doluluk %",
    days_until_checkin: "Giriş'e Kalan Gün",
    unsold_percent_by_date: "Satılmamış %",
    revenue_pace: "Gelir Hızı (geçen yıla göre)",
    weekday_demand: "Haftanın Günleri",
};
const ACTION_LABELS = {
    adjust_price_percent: "Fiyatı %X arttır/azalt",
    adjust_price_fixed: "Fiyata sabit TL ekle/çıkar",
    set_price_fixed: "Fiyatı sabit değere ayarla",
    apply_discount: "İndirim katmanı ekle",
};
const OP_LABELS = { ">=": "büyükse veya eşitse", "<=": "küçükse veya eşitse", "==": "eşitse", between: "arasında" };

export default function AdminYieldManagerPage() {
    const { hotelId } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState("rules");
    const [rules, setRules] = useState([]);
    const [hotel, setHotel] = useState(null);
    const [ratePlans, setRatePlans] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [audit, setAudit] = useState([]);
    const [impact, setImpact] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    const loadAll = async () => {
        if (!hotelId) return;
        setLoading(true);
        try {
            const [r, h, rp, t, a, im] = await Promise.all([
                api.get("/admin/yield/rules", { params: { hotel_id: hotelId } }),
                api.get(`/admin/hotels/${hotelId}`),
                api.get(`/admin/hotels/${hotelId}/rate-plans`),
                api.get("/admin/yield/templates"),
                api.get("/admin/yield/audit-log", { params: { hotel_id: hotelId, limit: 50 } }),
                api.get("/admin/yield/impact-summary", { params: { hotel_ids: hotelId, hours: 24 } }),
            ]);
            setRules(r.data.items || []);
            setHotel(h.data || null);
            setRatePlans(rp.data || []);
            setTemplates(t.data.templates || []);
            setAudit(a.data.items || []);
            setImpact((im.data.items || [])[0] || null);
        } finally { setLoading(false); }
    };
    useEffect(() => { loadAll(); }, [hotelId]); // eslint-disable-line

    const onToggle = async (id) => { await api.post(`/admin/yield/rules/${id}/toggle`); loadAll(); };
    const onDelete = async (id) => {
        if (!window.confirm("Bu kuralı silmek istediğinizden emin misiniz?")) return;
        await api.delete(`/admin/yield/rules/${id}`); loadAll();
    };
    const onRunNow = async (id) => {
        const r = await api.post(`/admin/yield/rules/${id}/run`);
        const fired = r.data?.fired;
        const cells = r.data?.summary?.cells_affected || 0;
        alert(fired ? `✓ Kural çalıştırıldı. ${cells} hücre güncellendi.` : "Kural çalıştırıldı ama koşullar sağlanmadı.");
        loadAll();
    };
    const onUndo = async (auditId) => {
        if (!window.confirm("Bu kuraldan kaynaklanan değişiklikleri geri almak istiyor musunuz?")) return;
        const r = await api.post(`/admin/yield/audit-log/${auditId}/undo`);
        alert(r.data?.ok ? `✓ ${r.data.reverted_cells} hücre eski haline çevrildi.` : `Hata: ${r.data?.reason || "?"}`);
        loadAll();
    };
    const onUseTemplate = (tpl) => { setEditingRule({ ...tpl, _fromTemplate: true, hotel_id: hotelId }); setShowWizard(true); };

    return (
        <AdminLayout title={hotel ? `${hotel.name} · Yield Manager` : "Yield Manager"} action={
            <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => navigate(`/admin/hotels/${hotelId}/calendar`)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-medium whitespace-nowrap">
                    <ArrowLeft className="h-4 w-4" /> Takvime Dön
                </button>
                <button onClick={() => { setEditingRule(null); setShowWizard(true); }} data-testid="yield-create-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold whitespace-nowrap">
                    <Plus className="h-4 w-4" /> Yeni Kural
                </button>
            </div>
        }>
            {/* Impact summary (last 24h) */}
            {impact && (
                <div data-testid="yield-impact-card" className={`mb-5 p-4 rounded-2xl border-2 ${impact.fires > 0 ? (impact.est_revenue_lift >= 0 ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20" : "border-rose-200 bg-rose-50/50 dark:border-rose-900 dark:bg-rose-950/20") : "border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-900/30"}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2">
                            <Sparkles className={`h-4 w-4 ${impact.fires > 0 && impact.est_revenue_lift >= 0 ? "text-emerald-500" : impact.fires > 0 ? "text-rose-500" : "text-slate-400"}`} />
                            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Son 24 Saat Etkisi</h3>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                            <div><span className="text-slate-500">Tetiklenme:</span> <b className="text-slate-900 dark:text-white">{impact.fires}</b></div>
                            <div><span className="text-slate-500">Hücre:</span> <b className="text-slate-900 dark:text-white">{impact.cells_affected}</b></div>
                            <div><span className="text-slate-500">Ort. Fiyat Δ:</span> <b className={impact.avg_delta_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{impact.avg_delta_pct >= 0 ? "+" : ""}{impact.avg_delta_pct}%</b></div>
                            <div><span className="text-slate-500">Tahmini Gelir:</span> <b className={impact.est_revenue_lift >= 0 ? "text-emerald-600" : "text-rose-600"}>{impact.est_revenue_lift >= 0 ? "+" : ""}{formatTRY(impact.est_revenue_lift)}</b></div>
                        </div>
                    </div>
                    {impact.fires === 0 && (
                        <p className="text-[11px] text-slate-500 mt-2">Bu pencerede tetiklenen kural yok. Mevcut kuralları aktif ettiğinizde / &ldquo;Şimdi çalıştır&rdquo; ile tetiklediğinizde etkisi burada görünür.</p>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-slate-200 dark:border-slate-800">
                {[
                    { k: "rules", label: "Aktif Kurallar", icon: TrendingUp },
                    { k: "templates", label: "Hazır Şablonlar", icon: Sparkles },
                    { k: "audit", label: "Tetikleme Geçmişi", icon: History },
                ].map(t => (
                    <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
                        className={`px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-2 ${tab === t.k ? "border-b-2 border-purple-500 text-purple-600 dark:text-purple-400" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
                        <t.icon className="h-4 w-4" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "rules" && (
                <RulesTab rules={rules} loading={loading} onToggle={onToggle} onDelete={onDelete} onRunNow={onRunNow}
                    onEdit={(r) => { setEditingRule(r); setShowWizard(true); }} />
            )}
            {tab === "templates" && (
                <TemplatesTab templates={templates} onUse={onUseTemplate} />
            )}
            {tab === "audit" && (
                <AuditTab audit={audit} onUndo={onUndo} />
            )}

            {showWizard && (
                <RuleWizard
                    hotel={hotel}
                    ratePlans={ratePlans}
                    initial={editingRule}
                    onClose={() => { setShowWizard(false); setEditingRule(null); }}
                    onSaved={() => { setShowWizard(false); setEditingRule(null); loadAll(); }}
                />
            )}
        </AdminLayout>
    );
}

function RulesTab({ rules, loading, onToggle, onDelete, onRunNow, onEdit }) {
    if (loading) return <div className="text-sm text-slate-500">Yükleniyor...</div>;
    if (!rules.length) return (
        <div className="text-center py-16 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border-2 border-dashed border-slate-200 dark:border-slate-800">
            <TrendingUp className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
            <h3 className="text-lg font-bold text-slate-700 dark:text-slate-300">Henüz Yield kuralı yok</h3>
            <p className="text-sm text-slate-500 mt-1">Yeni Kural butonu ile başlayın veya Hazır Şablonlar sekmesinden seçin.</p>
        </div>
    );
    return (
        <div className="grid gap-3">
            {rules.map(r => (
                <div key={r.id} data-testid={`yield-rule-${r.id}`}
                    className={`p-4 rounded-2xl border-2 transition-all ${r.is_active ? "border-purple-200 dark:border-purple-900 bg-purple-50/40 dark:bg-purple-950/10" : "border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/30 opacity-70"}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-slate-900 dark:text-white">{r.name}</h3>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{r.priority >= 1 && r.priority <= 5 ? `${r.priority}. Öncelik` : `#${r.priority}`}</span>
                                {r.auto_revert && <span title="Tetiklenmediğinde fiyatı otomatik geri alır" className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">↻ auto-revert</span>}
                                {r.last_triggered_at && <span className="text-[10px] text-slate-500">Son: {new Date(r.last_triggered_at).toLocaleString("tr-TR")}</span>}
                                <span className="text-[10px] text-slate-500">· {r.trigger_count || 0} kez tetiklendi</span>
                            </div>
                            <p className="text-xs text-slate-600 dark:text-slate-400">{r.description}</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {(r.triggers || []).map((t, i) => (
                                    <span key={i} className="px-2 py-0.5 rounded-md bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 text-[10px] font-semibold">
                                        {TRIGGER_LABELS[t.type] || t.type} {OP_LABELS[t.operator] || t.operator} {Array.isArray(t.value) ? t.value.join("–") : t.value}
                                    </span>
                                ))}
                                <span className="px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 text-[10px] font-semibold">⮕</span>
                                {(r.actions || []).map((a, i) => (
                                    <span key={i} className="px-2 py-0.5 rounded-md bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold">
                                        {ACTION_LABELS[a.type] || a.type}: {a.value > 0 && a.type === "adjust_price_percent" ? "+" : ""}{a.value}{a.type === "adjust_price_percent" || a.type === "apply_discount" ? "%" : ""}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => onToggle(r.id)} title={r.is_active ? "Pasifleştir" : "Aktifleştir"} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                {r.is_active ? <ToggleRight className="h-5 w-5 text-purple-500" /> : <ToggleLeft className="h-5 w-5 text-slate-400" />}
                            </button>
                            <button onClick={() => onRunNow(r.id)} title="Şimdi çalıştır" data-testid={`yield-run-${r.id}`} className="p-2 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/30 text-emerald-600"><Play className="h-4 w-4" /></button>
                            <button onClick={() => onEdit(r)} title="Düzenle" className="p-2 rounded-lg hover:bg-sky-100 dark:hover:bg-sky-950/30 text-sky-600"><Eye className="h-4 w-4" /></button>
                            <button onClick={() => onDelete(r.id)} title="Sil" className="p-2 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-950/30 text-rose-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function TemplatesTab({ templates, onUse }) {
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(t => (
                <div key={t.key} data-testid={`yield-template-${t.key}`} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-purple-300 transition-colors">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        <h3 className="font-bold text-sm text-slate-900 dark:text-white">{t.name}</h3>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">{t.description}</p>
                    <button onClick={() => onUse(t)} className="w-full py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold">+ Bu Şablonu Kullan</button>
                </div>
            ))}
        </div>
    );
}

function AuditTab({ audit, onUndo }) {
    if (!audit.length) return <div className="text-sm text-slate-500 text-center py-12">Henüz tetikleme kaydı yok.</div>;
    return (
        <div className="grid gap-2">
            {audit.map(a => (
                <div key={a.id} className={`p-3 rounded-xl border ${a.should_fire ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50/30 dark:bg-emerald-950/10" : "border-slate-200 dark:border-slate-800 bg-slate-50/40"} ${a.undone ? "opacity-50" : ""}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-xs">
                                {a.should_fire ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                                <span className="font-bold">{a.rule_name}</span>
                                {a.forced && <span className="px-1.5 py-0.5 rounded bg-sky-100 dark:bg-sky-950/40 text-sky-700 text-[9px] font-bold uppercase">manuel</span>}
                                {a.undone && <span className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[9px] font-bold uppercase">geri alındı</span>}
                                <span className="text-slate-500">{new Date(a.triggered_at).toLocaleString("tr-TR")}</span>
                            </div>
                            <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">
                                {a.should_fire ? `${a.cells_affected} hücre güncellendi` : "Koşullar sağlanmadı"}
                                {(a.evaluation || []).slice(0, 3).map((e, i) => (
                                    <span key={i} className="ml-2">· {TRIGGER_LABELS[e.type] || e.type}: <b>{e.actual}</b> {e.operator} {e.value} {e.matched ? "✓" : "✗"}</span>
                                ))}
                            </div>
                        </div>
                        {a.should_fire && !a.undone && a.cells_affected > 0 && (
                            <button onClick={() => onUndo(a.id)} data-testid={`yield-undo-${a.id}`} className="px-2.5 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-bold flex items-center gap-1">
                                <Undo2 className="h-3.5 w-3.5" /> Geri Al
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ----- Wizard -----
function RuleWizard({ hotel, ratePlans = [], initial, onClose, onSaved }) {
    const fromTemplate = initial?._fromTemplate;
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(() => {
        const base = initial || {};
        return {
            id: base.id || null,
            hotel_id: hotel?.id || base.hotel_id || "",
            name: base.name || "",
            description: base.description || "",
            is_active: base.is_active !== false,
            priority: base.priority && base.priority <= 5 ? base.priority : 3,
            active_from: base.active_from || "",
            active_to: base.active_to || "",
            scope: {
                date_from: base.scope?.date_from || "",
                date_to: base.scope?.date_to || "",
                weekdays: base.scope?.weekdays || [0, 1, 2, 3, 4, 5, 6],
                room_type_ids: base.scope?.room_type_ids || [],
                rate_plan_ids: base.scope?.rate_plan_ids || [],
                ...(base.scope_defaults || {}),
            },
            triggers: base.triggers || [{ type: "rooms_sold", operator: ">=", value: 10 }],
            actions: base.actions || [{ type: "adjust_price_percent", value: 15, target: "all_persons" }],
            guardrails: base.guardrails || { max_increase_percent: 50, max_decrease_percent: 30, cooldown_hours: 24 },
            schedule: base.schedule || { mode: "realtime" },
            auto_revert: base.auto_revert === true,
        };
    });
    const [saving, setSaving] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [error, setError] = useState("");

    const update = (path, value) => {
        setForm(f => {
            const c = JSON.parse(JSON.stringify(f));
            const keys = path.split(".");
            let cur = c;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys[keys.length - 1]] = value;
            return c;
        });
    };

    const submit = async () => {
        setSaving(true); setError("");
        try {
            const body = { ...form };
            delete body._fromTemplate;
            delete body.scope_defaults;
            if (form.id && !fromTemplate) {
                await api.put(`/admin/yield/rules/${form.id}`, body);
            } else {
                delete body.id;
                await api.post("/admin/yield/rules", body);
            }
            onSaved();
        } catch (e) {
            setError(e.response?.data?.detail || "Hata");
        } finally { setSaving(false); }
    };

    const doPreview = async () => {
        try {
            const body = { ...form };
            delete body._fromTemplate;
            delete body.scope_defaults;
            delete body.id;
            const r = await api.post("/admin/yield/preview", body);
            setPreviewResult(r.data);
        } catch (e) {
            setPreviewResult({ error: e.response?.data?.detail || "Hata" });
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-4">
            <div className="w-full sm:max-w-2xl bg-white dark:bg-slate-950 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
                    <h2 className="font-bold text-base">{form.id && !fromTemplate ? "Kural Düzenle" : "Yeni Yield Kuralı"}</h2>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                </div>

                {/* Steps */}
                <div className="flex gap-1.5 p-3 border-b border-slate-100 dark:border-slate-900">
                    {[1, 2, 3, 4].map(n => (
                        <div key={n} className={`flex-1 h-1.5 rounded-full ${n <= step ? "bg-purple-500" : "bg-slate-200 dark:bg-slate-800"}`} />
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {/* Step 1: Scope */}
                    {step === 1 && (
                        <>
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">1. Kapsam</h3>
                            <Field label="Kural Adı"><input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="örn: Yaz Yoğunluk +15" className={INP} /></Field>
                            <Field label="Açıklama (opsiyonel)"><input value={form.description} onChange={(e) => update("description", e.target.value)} className={INP} /></Field>
                            <Field label="Otel">
                                <div className="px-2.5 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-semibold text-slate-700 dark:text-slate-200">{hotel?.name || "—"}</div>
                            </Field>

                            {/* Active window: when the campaign runs */}
                            <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-purple-700 dark:text-purple-300">📅 Kuralın Aktif Olduğu Dönem</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mb-2">Kural sadece bu tarih aralığında çalışır (kampanya süresi). Boş bırakılırsa süresiz aktif.</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <Field label="Aktif Başlangıç"><input data-testid="active-from" type="date" value={form.active_from} onChange={(e) => update("active_from", e.target.value)} className={INP} /></Field>
                                    <Field label="Aktif Bitiş"><input data-testid="active-to" type="date" value={form.active_to} onChange={(e) => update("active_to", e.target.value)} className={INP} /></Field>
                                </div>
                            </div>

                            {/* Stay window: which check-in dates this rule affects */}
                            <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-900">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300">🛏️ Konaklama Tarihi</span>
                                </div>
                                <p className="text-[10px] text-slate-500 mb-2">Kural hangi check-in tarihlerini etkileyecek (rezervasyon takvimindeki günler).</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <Field label="Konaklama Başlangıç"><input data-testid="stay-from" type="date" value={form.scope.date_from} onChange={(e) => update("scope.date_from", e.target.value)} className={INP} /></Field>
                                    <Field label="Konaklama Bitiş"><input data-testid="stay-to" type="date" value={form.scope.date_to} onChange={(e) => update("scope.date_to", e.target.value)} className={INP} /></Field>
                                </div>
                                <div className="mt-2">
                                    <Field label="Geçerli Günler">
                                        <div className="flex gap-1.5 flex-wrap">
                                            {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d, i) => {
                                                const sel = (form.scope.weekdays || []).includes(i);
                                                return (
                                                    <button key={i} type="button" onClick={() => {
                                                        const cur = form.scope.weekdays || [];
                                                        update("scope.weekdays", sel ? cur.filter(x => x !== i) : [...cur, i].sort());
                                                    }} className={`px-2.5 py-1.5 rounded-md text-xs font-bold ${sel ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-800 text-slate-600 border border-slate-200 dark:border-slate-700"}`}>{d}</button>
                                                );
                                            })}
                                        </div>
                                    </Field>
                                </div>
                            </div>

                            {/* Room types selector */}
                            {(hotel?.room_types || []).length > 0 && (
                                <Field label="Oda Tipleri (boş bırakılırsa tüm odalar)">
                                    <div className="flex flex-wrap gap-1.5">
                                        {(hotel?.room_types || []).map((rt) => {
                                            const sel = (form.scope.room_type_ids || []).includes(rt.id);
                                            return (
                                                <button key={rt.id} type="button" data-testid={`yield-room-${rt.id}`} onClick={() => {
                                                    const cur = form.scope.room_type_ids || [];
                                                    update("scope.room_type_ids", sel ? cur.filter(x => x !== rt.id) : [...cur, rt.id]);
                                                }} className={`px-2.5 py-1.5 rounded-md text-xs font-semibold ${sel ? "bg-emerald-500 text-white" : "bg-white dark:bg-slate-800 text-slate-600 border border-slate-200 dark:border-slate-700"}`}>{rt.name}</button>
                                            );
                                        })}
                                    </div>
                                </Field>
                            )}

                            {/* Rate plans selector */}
                            {ratePlans.length > 0 && (
                                <Field label="Konaklama Planları (boş bırakılırsa tüm planlar)">
                                    <div className="flex flex-wrap gap-1.5">
                                        {ratePlans.map((rp) => {
                                            const sel = (form.scope.rate_plan_ids || []).includes(rp.id);
                                            return (
                                                <button key={rp.id} type="button" data-testid={`yield-rate-${rp.id}`} onClick={() => {
                                                    const cur = form.scope.rate_plan_ids || [];
                                                    update("scope.rate_plan_ids", sel ? cur.filter(x => x !== rp.id) : [...cur, rp.id]);
                                                }} className={`px-2.5 py-1.5 rounded-md text-xs font-semibold ${sel ? "bg-amber-500 text-white" : "bg-white dark:bg-slate-800 text-slate-600 border border-slate-200 dark:border-slate-700"}`}>{rp.name}{rp.board ? <span className="opacity-75 ml-1">({rp.board})</span> : null}</button>
                                            );
                                        })}
                                    </div>
                                </Field>
                            )}

                            {/* 5-level priority */}
                            <Field label="Öncelik (Çakışan kurallar için)">
                                <select data-testid="yield-priority" value={form.priority} onChange={(e) => update("priority", Number(e.target.value))} className={INP}>
                                    <option value={1}>1. Öncelik — En Yüksek (önce uygulanır)</option>
                                    <option value={2}>2. Öncelik — Yüksek</option>
                                    <option value={3}>3. Öncelik — Standart (varsayılan)</option>
                                    <option value={4}>4. Öncelik — Düşük</option>
                                    <option value={5}>5. Öncelik — En Düşük (en son)</option>
                                </select>
                                <p className="text-[10px] text-slate-500 mt-1">Aynı hücreye birden fazla kural denk gelirse, yüksek öncelik (1) önce uygulanır ve düşük öncelikli kurallar o hücreyi atlar.</p>
                            </Field>
                        </>
                    )}

                    {/* Step 2: Triggers */}
                    {step === 2 && (
                        <>
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">2. Tetikleyici(ler)</h3>
                            <p className="text-[11px] text-slate-500">Birden fazla tetikleyici eklerseniz <b>HEPSİ</b> aynı anda sağlanmalı (AND).</p>
                            {form.triggers.map((t, idx) => (
                                <div key={idx} className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold uppercase text-sky-700">Tetikleyici #{idx + 1}</span>
                                        {form.triggers.length > 1 && <button onClick={() => update("triggers", form.triggers.filter((_, i) => i !== idx))} className="text-rose-500 text-xs"><Trash2 className="h-3.5 w-3.5" /></button>}
                                    </div>
                                    <select value={t.type} onChange={(e) => {
                                        const next = [...form.triggers];
                                        next[idx] = { ...next[idx], type: e.target.value };
                                        update("triggers", next);
                                    }} className={INP}>
                                        {Object.entries(TRIGGER_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    <div className="grid grid-cols-2 gap-2">
                                        <select value={t.operator} onChange={(e) => { const n = [...form.triggers]; n[idx] = { ...n[idx], operator: e.target.value }; update("triggers", n); }} className={INP}>
                                            <option value=">=">Büyükse veya eşitse</option>
                                            <option value="<=">Küçükse veya eşitse</option>
                                            <option value="==">Eşitse</option>
                                        </select>
                                        <input type="number" value={t.value} onChange={(e) => { const n = [...form.triggers]; n[idx] = { ...n[idx], value: Number(e.target.value) }; update("triggers", n); }} className={INP} placeholder="Değer" />
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => update("triggers", [...form.triggers, { type: "occupancy_percent", operator: ">=", value: 70 }])} className="w-full py-2 rounded-lg border-2 border-dashed border-sky-300 text-sky-600 text-xs font-bold">+ Tetikleyici Ekle</button>
                        </>
                    )}

                    {/* Step 3: Actions */}
                    {step === 3 && (
                        <>
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">3. Aksiyon(lar)</h3>
                            {form.actions.map((a, idx) => (
                                <div key={idx} className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold uppercase text-amber-700">Aksiyon #{idx + 1}</span>
                                        {form.actions.length > 1 && <button onClick={() => update("actions", form.actions.filter((_, i) => i !== idx))} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                                    </div>
                                    <select value={a.type} onChange={(e) => { const n = [...form.actions]; n[idx] = { ...n[idx], type: e.target.value }; update("actions", n); }} className={INP}>
                                        {Object.entries(ACTION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                    <input type="number" value={a.value} onChange={(e) => { const n = [...form.actions]; n[idx] = { ...n[idx], value: Number(e.target.value) }; update("actions", n); }} className={INP} placeholder="Değer (örn: +15 = %15 zam, -10 = %10 indirim)" />
                                </div>
                            ))}
                            <button onClick={() => update("actions", [...form.actions, { type: "adjust_price_percent", value: 10, target: "all_persons" }])} className="w-full py-2 rounded-lg border-2 border-dashed border-amber-300 text-amber-600 text-xs font-bold">+ Aksiyon Ekle</button>
                        </>
                    )}

                    {/* Step 4: Guardrails + Preview */}
                    {step === 4 && (
                        <>
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">4. Güvenlik Limitleri</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Max Artış (%)"><input type="number" value={form.guardrails.max_increase_percent || ""} onChange={(e) => update("guardrails.max_increase_percent", e.target.value === "" ? null : Number(e.target.value))} className={INP} placeholder="50" /></Field>
                                <Field label="Max Düşüş (%)"><input type="number" value={form.guardrails.max_decrease_percent || ""} onChange={(e) => update("guardrails.max_decrease_percent", e.target.value === "" ? null : Number(e.target.value))} className={INP} placeholder="30" /></Field>
                                <Field label="Min Fiyat (TL)"><input type="number" value={form.guardrails.min_price || ""} onChange={(e) => update("guardrails.min_price", e.target.value === "" ? null : Number(e.target.value))} className={INP} placeholder="500" /></Field>
                                <Field label="Max Fiyat (TL)"><input type="number" value={form.guardrails.max_price || ""} onChange={(e) => update("guardrails.max_price", e.target.value === "" ? null : Number(e.target.value))} className={INP} placeholder="10000" /></Field>
                                <Field label="Cooldown (saat)"><input type="number" value={form.guardrails.cooldown_hours || 0} onChange={(e) => update("guardrails.cooldown_hours", Number(e.target.value))} className={INP} /></Field>
                                <Field label="Çalışma Modu">
                                    <select value={form.schedule.mode || "realtime"} onChange={(e) => update("schedule.mode", e.target.value)} className={INP}>
                                        <option value="realtime">Realtime (her rez. sonrası)</option>
                                        <option value="manual">Manuel (sadece elle)</option>
                                    </select>
                                </Field>
                            </div>
                            <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-800">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="yield-auto-revert-toggle"
                                        checked={form.auto_revert}
                                        onChange={(e) => update("auto_revert", e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-purple-500 focus:ring-purple-500"
                                    />
                                    <div className="flex-1">
                                        <div className="text-xs font-bold text-slate-700 dark:text-slate-200">Otomatik Geri Alma (Auto-Revert)</div>
                                        <div className="text-[10px] text-slate-500 mt-0.5">Kural tetiklenmiyorsa, en son uygulanan değişiklikleri orijinal fiyatlara geri alır. Doluluk düştüğünde fiyat otomatik iner.</div>
                                    </div>
                                </label>
                            </div>
                            <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
                                <button onClick={doPreview} className="w-full py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold">🔍 Şimdi Önizle (uygulamadan değerlendir)</button>
                                {previewResult && (
                                    <div className={`mt-2 p-3 rounded-lg text-xs ${previewResult.should_fire ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200" : "bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"}`}>
                                        {previewResult.error ? `Hata: ${previewResult.error}` : (
                                            <>
                                                <div className="font-bold">{previewResult.should_fire ? "✓ Şimdi tetiklenirdi" : "⚠ Şu an tetiklenmezdi"}</div>
                                                {(previewResult.details || []).map((d, i) => <div key={i}>· {TRIGGER_LABELS[d.type] || d.type}: <b>{d.actual}</b> {d.operator} {d.value} → {d.matched ? "✓" : "✗"}</div>)}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {error && <div className="text-xs font-bold text-rose-600 p-2 rounded bg-rose-50 dark:bg-rose-950/30">{error}</div>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-2 p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
                    <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="px-3 py-2 rounded-lg bg-slate-200 dark:bg-slate-800 text-xs font-bold disabled:opacity-40">← Geri</button>
                    {step < 4
                        ? <button onClick={() => setStep(step + 1)} className="px-4 py-2 rounded-lg bg-purple-500 text-white text-xs font-bold">İleri →</button>
                        : <button onClick={submit} disabled={saving} data-testid="yield-save-btn" className="px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold disabled:opacity-50">{saving ? "Kaydediliyor..." : "Kuralı Kaydet"}</button>}
                </div>
            </div>
        </div>
    );
}

const INP = "w-full px-2.5 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-900 dark:text-white";
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
            {children}
        </div>
    );
}
